import { useState, useEffect, useCallback } from 'react';
import {
  Users, TrendingUp, UserCog, CheckCircle, Award, XCircle, Target, Zap, PieChart, BarChart3, GraduationCap
} from 'lucide-react';
import {
  ResponsiveContainer, PieChart as RPieChart, Pie, Cell, Tooltip, Legend,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, AreaChart, Area
} from 'recharts';
import { useTheme } from '../../context/ThemeContext';
import { dashboardService } from '../../services/api';
import { KPICard, Skeleton, EmptyState } from '../../components/ui';
import { COLORS, STATUS_COLORS, STATUS_NAMES, MONTH_ABBR } from '../../constants';
import DashboardDateFilter from './DashboardDateFilter';

const CURRENT_YEAR = new Date().getFullYear();
const CURRENT_MONTH = new Date().getMonth();

const defaultParams = () => {
  const start = new Date(CURRENT_YEAR, CURRENT_MONTH, 1);
  const end = new Date(CURRENT_YEAR, CURRENT_MONTH + 1, 0, 23, 59, 59);
  return {
    startDate: start.toISOString().split('T')[0],
    endDate: end.toISOString().split('T')[0],
  };
};

const fmtTrend = (value) => {
  if (value === undefined || value === null || Number.isNaN(value)) return {};
  return { trend: `${value >= 0 ? '+' : ''}${value}%`, trendUp: value >= 0 };
};

const monthlyTrendToChartData = (monthlyTrend) =>
  (monthlyTrend || []).slice().reverse().map(m => ({
    month: `${MONTH_ABBR[m._id.month - 1]}`,
    count: m.count,
    admissions: m.admissions || 0,
  }));

const SuperAdminDashboard = () => {
  const { dark } = useTheme();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [params, setParams] = useState(defaultParams);

  const fetchData = useCallback((p) => {
    setLoading(true);
    setData(null);
    dashboardService.getSuperAdmin(p)
      .then(({ data: res }) => setData(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchData(params); }, [fetchData, params]);

  const cs = { background: dark ? '#1E293B' : '#fff', borderRadius: 14, padding: 20, border: `1px solid ${dark ? '#334155' : '#E2E8F0'}` };
  const tt = { background: dark ? '#1E293B' : '#fff', border: `1px solid ${dark ? '#334155' : '#E2E8F0'}`, borderRadius: 8, fontSize: 12 };

  const kpis = data?.kpis || {};
  const trends = kpis.trends || {};
  const sourceData = (data?.charts?.sourceDistribution || []).map(s => ({ name: s._id || 'Unknown', count: s.count }));
  const courseData = (data?.charts?.coursePerformance || []).map(c => ({ name: c._id || 'Unknown', total: c.total, admissions: c.admissions, lost: c.lost }));
  const monthlyData = monthlyTrendToChartData(data?.charts?.monthlyTrend);
  const statusData = (data?.charts?.statusDistribution || []).map(s => ({
    name: STATUS_NAMES[s._id] || s._id, value: s.count, color: STATUS_COLORS[s._id] || '#6B7280',
  }));

  return (
    <div>
      {/* Header + filter */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12, marginBottom: 24 }}>
        <div>
          <h2 style={{ fontSize: 22, fontWeight: 700, color: dark ? '#F1F5F9' : '#0F172A', marginBottom: 4 }}>Dashboard Overview</h2>
          <p style={{ fontSize: 13, color: dark ? '#64748B' : '#94A3B8', margin: 0 }}>System-wide performance metrics and analytics</p>
        </div>
        <DashboardDateFilter onChange={p => setParams(p)} />
      </div>

      {loading ? (
        <Skeleton rows={8} cols={4} />
      ) : !data ? (
        <EmptyState title="Dashboard failed to load" />
      ) : (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(200px,1fr))', gap: 14, marginBottom: 28 }}>
            <KPICard icon={Users} label="Total Leads" value={kpis.totalLeads || 0} color="#3B82F6" {...fmtTrend(trends.totalLeads)} />
            <KPICard icon={Zap} label="Today's Leads" value={kpis.todayLeads || 0} color="#8B5CF6" />
            {/* <KPICard icon={TrendingUp} label="Period Leads" value={kpis.monthlyLeads || 0} color="#06B6D4" {...fmtTrend(trends.monthlyLeads)} /> */}
            {/* <KPICard icon={UserCog} label="Total Users" value={kpis.totalUsers || 0} color="#F59E0B" /> */}
            <KPICard icon={CheckCircle} label="Admissions" value={kpis.admissions || 0} color="#10B981" {...fmtTrend(trends.admissions)} />
            <KPICard icon={Award} label="Registrations" value={kpis.registrations || 0} color="#14B8A6" {...fmtTrend(trends.registrations)} />
            <KPICard icon={XCircle} label="Lost Leads" value={kpis.lostLeads || 0} color="#EF4444" {...fmtTrend(trends.lostLeads)} />
            <KPICard icon={Target} label="Conversion Rate" value={`${kpis.conversionRate || 0}%`} color="#22C55E" {...fmtTrend(trends.conversionRate)} />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 24 }}>
            <div style={cs}>
              <h4 style={{ margin: '0 0 16px', fontSize: 14, fontWeight: 600, color: dark ? '#F1F5F9' : '#0F172A' }}>Lead Source Distribution</h4>
              {sourceData.length === 0 ? <EmptyState icon={PieChart} title="No lead source data" /> : (
                <>
                  <ResponsiveContainer width="100%" height={240}>
                    <RPieChart>
                      <Pie data={sourceData} cx="50%" cy="50%" innerRadius={55} outerRadius={95} paddingAngle={2} dataKey="count" nameKey="name">
                        {sourceData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                      </Pie>
                      <Tooltip contentStyle={tt} />
                    </RPieChart>
                  </ResponsiveContainer>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
                    {sourceData.slice(0, 6).map((s, i) => (
                      <div key={s.name} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: dark ? '#94A3B8' : '#64748B' }}>
                        <div style={{ width: 8, height: 8, borderRadius: 4, background: COLORS[i % COLORS.length] }} /> {s.name}: {s.count}
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>

            <div style={cs}>
              <h4 style={{ margin: '0 0 16px', fontSize: 14, fontWeight: 600, color: dark ? '#F1F5F9' : '#0F172A' }}>Course-wise Performance</h4>
              {courseData.length === 0 ? <EmptyState icon={GraduationCap} title="No course data" /> : (
                <ResponsiveContainer width="100%" height={270}>
                  <BarChart data={courseData} layout="vertical" margin={{ left: 80 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={dark ? '#334155' : '#E2E8F0'} />
                    <XAxis type="number" tick={{ fontSize: 11, fill: dark ? '#94A3B8' : '#64748B' }} allowDecimals={false} />
                    <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: dark ? '#94A3B8' : '#64748B' }} width={75} />
                    <Tooltip contentStyle={tt} />
                    <Legend />
                    <Bar dataKey="total" fill="#3B82F6" radius={[0, 4, 4, 0]} name="Total" />
                    <Bar dataKey="admissions" fill="#10B981" radius={[0, 4, 4, 0]} name="Admissions" />
                    <Bar dataKey="lost" fill="#EF4444" radius={[0, 4, 4, 0]} name="Lost" />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
            <div style={cs}>
              <h4 style={{ margin: '0 0 16px', fontSize: 14, fontWeight: 600, color: dark ? '#F1F5F9' : '#0F172A' }}>Monthly Trends</h4>
              {monthlyData.length === 0 ? <EmptyState icon={TrendingUp} title="No trend data" /> : (
                <ResponsiveContainer width="100%" height={230}>
                  <AreaChart data={monthlyData}>
                    <CartesianGrid strokeDasharray="3 3" stroke={dark ? '#334155' : '#E2E8F0'} />
                    <XAxis dataKey="month" tick={{ fontSize: 11, fill: dark ? '#94A3B8' : '#64748B' }} />
                    <YAxis tick={{ fontSize: 11, fill: dark ? '#94A3B8' : '#64748B' }} allowDecimals={false} />
                    <Tooltip contentStyle={tt} />
                    <Legend />
                    <Area type="monotone" dataKey="count" stroke="#3B82F6" fill="#3B82F6" fillOpacity={0.1} name="Leads" />
                    <Area type="monotone" dataKey="admissions" stroke="#10B981" fill="#10B981" fillOpacity={0.1} name="Admissions" />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </div>

            <div style={cs}>
              <h4 style={{ margin: '0 0 16px', fontSize: 14, fontWeight: 600, color: dark ? '#F1F5F9' : '#0F172A' }}>Lead Status Breakdown</h4>
              {statusData.length === 0 ? <EmptyState icon={BarChart3} title="No status data" /> : (
                <ResponsiveContainer width="100%" height={230}>
                  <BarChart data={statusData}>
                    <CartesianGrid strokeDasharray="3 3" stroke={dark ? '#334155' : '#E2E8F0'} />
                    <XAxis dataKey="name" tick={{ fontSize: 9, fill: dark ? '#94A3B8' : '#64748B' }} angle={-20} textAnchor="end" height={50} />
                    <YAxis tick={{ fontSize: 11, fill: dark ? '#94A3B8' : '#64748B' }} allowDecimals={false} />
                    <Tooltip contentStyle={tt} />
                    <Bar dataKey="value" radius={[6, 6, 0, 0]} name="Leads">
                      {statusData.map((s, i) => <Cell key={i} fill={s.color} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default SuperAdminDashboard;
